import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {WithChildren} from '../../../../_metronic/helpers'
import {useMsal} from '@azure/msal-react'
import {CacheLookupPolicy, InteractionRequiredAuthError, InteractionStatus} from '@azure/msal-browser'
import api from '../../../../services/apiConfig'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: () => void
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const logout = () => {
    saveAuth(undefined)
    setCurrentUser(undefined)
  }

  return (
    <AuthContext.Provider value={{auth, saveAuth, currentUser, setCurrentUser, logout}}>
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<{children: React.ReactNode}> = ({children}) => {
  const {instance, accounts, inProgress} = useMsal()
  const {currentUser, setCurrentUser, logout} = useAuth()
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  const didRequest = useRef(false)

  const BASE_URL = process.env.REACT_APP_API_URL
  const scope = process.env.REACT_APP_SCOPE as string

  useEffect(() => {
    const initSession = async () => {
      try {
        if (accounts.length === 0) {
          await instance.loginRedirect()
          return
        }

        const request = {
          scopes: [scope],
          account: accounts[0],
          forceRefresh: true,
          cacheLookupPolicy: CacheLookupPolicy.Default // will default to CacheLookupPolicy.Default if omitted
        }

        const tokenResponse = await instance.acquireTokenSilent(request).catch(error => {
            if (error instanceof InteractionRequiredAuthError) {
                // fallback to interaction when silent call fails
                return instance.acquireTokenRedirect(request)
            }
        });

        const token = tokenResponse?.accessToken
        if (!token) throw new Error('Token vacío al intentar verificar usuario')

        const response = await api.get<{usuario: any}>(`${BASE_URL}/verificarUsuario`, {
          headers: {Authorization: `Bearer ${token}`},
        })

        setCurrentUser(response.data.usuario ?? null)
        setShowSplashScreen(false)
      } catch (err) {
        console.error('[AuthInit] Error en autenticación:', err)
        logout()
        setShowSplashScreen(false)
      }
    }

    if (inProgress === InteractionStatus.None && !didRequest.current) {
      didRequest.current = true
      initSession()
    }
  }, [accounts, instance, inProgress, logout, setCurrentUser])

  return showSplashScreen && !currentUser ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
